import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Printer, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const gbp = (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(v || 0);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
const fmtDay = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" }) : "—");

/** Printable invoice / receipt for a booking — charges, payments received and
 *  refunds issued. Works for active AND cancelled stays (cancellation refund
 *  evidence). Printed via window.print → only the sheet prints (print CSS). */
const AdminInvoice = () => {
  const { bookingId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/bookings/${bookingId}/invoice-data`);
        setData(res.data);
      } catch {
        setError("Couldn't load this invoice.");
      }
    })();
  }, [bookingId]);

  if (error) {
    return <div className="min-h-screen bg-[#F5EFE6] flex items-center justify-center text-[#8C2E39]">{error}</div>;
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#F5EFE6] flex items-center justify-center text-[#6A5F58]">
        <Loader2 className="animate-spin mr-2" size={18} /> Preparing invoice…
      </div>
    );
  }

  const net = Math.max(0, data.total - data.paid_total);

  return (
    <div className="min-h-screen bg-[#EDE3D2]/40 py-8">
      {/* Screen toolbar */}
      <div className="max-w-3xl mx-auto mb-4 flex items-center justify-between px-4 no-print" data-testid="invoice-toolbar">
        <Link to="/admin?tab=bookings" className="inline-flex items-center gap-2 text-sm text-[#6A5F58] hover:text-[#8C4E58]">
          <ArrowLeft size={14} /> Back to bookings
        </Link>
        <button
          onClick={() => window.print()}
          className="h-10 px-5 rounded-xl bg-[#0F0E0E] text-[#F5EFE6] text-[11px] uppercase tracking-[0.14em] inline-flex items-center gap-2 hover:bg-[#8C4E58] transition-colors"
          data-testid="invoice-print"
        >
          <Printer size={14} /> Print / Save PDF
        </button>
      </div>

      {/* The sheet — the only thing that prints */}
      <div className="invoice-sheet max-w-3xl mx-auto bg-white shadow-md p-8" data-testid="invoice-sheet">
        <div className="revenue-report-body">
          <h1>Walnut Lakes</h1>
          <p className="sheet-sub">Holiday Park · Saxilby, Lincolnshire · bookings@walnutlakes.co.uk</p>
          <h2>
            {data.payment_status === "cancelled" ? "Invoice & cancellation record" : "Invoice / receipt"} — {data.booking_id || "booking"}
          </h2>
          <p className="sheet-sub">Generated {fmt(data.generated_at)}</p>

          <table>
            <tbody>
              <tr><th style={{ width: "30%" }}>Guest</th><td>{data.guest_name}{data.guest_email ? ` · ${data.guest_email}` : ""}{data.guest_phone ? ` · ${data.guest_phone}` : ""}</td></tr>
              <tr><th>Stay</th><td>{data.unit_label} · {fmtDay(data.start_date)} → {fmtDay(data.end_date)} · {data.guests} guest{data.guests === 1 ? "" : "s"}{data.dogs ? ` · ${data.dogs} dog${data.dogs === 1 ? "" : "s"}` : ""}</td></tr>
              {data.payment_status === "cancelled" && (
                <tr><th>Cancelled</th><td>{fmt(data.cancelled_at)}{data.cancel_reason ? ` — “${data.cancel_reason}”` : ""}</td></tr>
              )}
            </tbody>
          </table>

          <h2>Charges</h2>
          <table data-testid="invoice-charges">
            <thead><tr><th>Item</th><th className="num">Amount</th></tr></thead>
            <tbody>
              {data.lines.map((l, i) => (
                <tr key={i}><td>{l.label}</td><td className="num">{l.amount < 0 ? `−${gbp(Math.abs(l.amount))}` : gbp(l.amount)}</td></tr>
              ))}
              <tr><td><strong>Total</strong></td><td className="num"><strong>{gbp(data.total)}</strong></td></tr>
            </tbody>
          </table>

          <h2>Payments received</h2>
          <table data-testid="invoice-payments">
            <thead><tr><th>When</th><th>How</th><th>Ref</th><th className="num">Amount</th></tr></thead>
            <tbody>
              {data.payments.length === 0 && <tr><td colSpan={4}>No payments recorded.</td></tr>}
              {data.payments.map((p, i) => (
                <tr key={i}><td>{fmt(p.at)}</td><td>{p.label}</td><td>{p.ref ? `…${p.ref}` : ""}</td><td className="num">{gbp(p.amount)}</td></tr>
              ))}
              <tr><td colSpan={3}><strong>Total received</strong></td><td className="num"><strong>{gbp(data.paid_total)}</strong></td></tr>
            </tbody>
          </table>

          <h2>Refunds issued</h2>
          <table data-testid="invoice-refunds">
            <thead><tr><th>When</th><th>Reason</th><th>Square ref</th><th className="num">Amount</th></tr></thead>
            <tbody>
              {data.refunds.length === 0 && <tr><td colSpan={4}>{data.payment_status === "cancelled" ? "No refund issued." : "No refunds."}</td></tr>}
              {data.refunds.map((r, i) => (
                <tr key={i}><td>{fmt(r.at)}</td><td>{r.reason || "—"}{r.by ? ` · by ${r.by}` : ""}</td><td>{r.ref || "—"}</td><td className="num">−{gbp(r.amount)}</td></tr>
              ))}
              {data.refunds.length > 0 && (
                <tr><td colSpan={3}><strong>Total refunded</strong></td><td className="num"><strong>−{gbp(data.refunded_total)}</strong></td></tr>
              )}
            </tbody>
          </table>

          <div className="kpi-grid">
            <div className="kpi-box"><p className="kpi-label">Booking total</p><p className="kpi-value">{gbp(data.total)}</p></div>
            <div className="kpi-box"><p className="kpi-label">Received</p><p className="kpi-value">{gbp(data.paid_total)}</p></div>
            <div className="kpi-box"><p className="kpi-label">Refunded</p><p className="kpi-value">{gbp(data.refunded_total)}</p></div>
            <div className="kpi-box">
              <p className="kpi-label">{data.payment_status === "cancelled" ? "Net kept" : "Balance due"}</p>
              <p className="kpi-value">{gbp(data.payment_status === "cancelled" ? Math.max(0, data.paid_total - data.refunded_total) : net)}</p>
            </div>
          </div>

          <p className="sheet-footer">
            Generated from the Walnut Lakes admin console · card payments are processed by Square · refunds return to the original card within 2–5 working days.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminInvoice;
